

import React, { useState, useEffect, useRef } from 'react';
import { ProjectData, StepStage, StepResult, FinancialParams } from './types';
import { initializeChat, sendMessageToGemini, generateAIFloorPlan } from './services/geminiService';
import { STEP_PROMPTS } from './constants';
import { InitForm } from './components/InitForm';
import { StepIndicator } from './components/StepIndicator';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import { StepActions } from './components/StepActions';
import { FinancialParamsForm } from './components/FinancialParamsForm';
import { generateHtmlReport } from './utils/reportGenerator';
import { Lightbulb } from 'lucide-react';

declare global {
  interface Window {
    XLSX: any;
    html2pdf: any;
  }
}

const App: React.FC = () => {
  const [stage, setStage] = useState<StepStage>(StepStage.INIT);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [financialParams, setFinancialParams] = useState<FinancialParams | null>(null);
  const [isCollectingFinancials, setIsCollectingFinancials] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<StepResult[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  
  const prevLoadingRef = useRef(false);

  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      setTimeout(() => {
        const element = document.getElementById(`step-container-${stage}`);
        if (element) {
            const headerOffset = 120; 
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
      }, 100);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, stage]);

  const handleInit = async (data: ProjectData) => {
    setIsLoading(true);
    setProjectData(data);
    
    const currentDate = new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

    const context = `
    当前系统日期 (Today): ${currentDate}
    (⚠️ 警告: 所有关于"未来/预计/开通时间"的判断，必须严格基于上述当前日期。若今天是2026年，则2025年的计划应视为"过去"或"已完成")
    
    项目名称/代号: ${data.projectName}
    项目位置: ${data.location}
    体量: ${data.scale}
    标准层面积: ${data.standardFloorArea || '未提供'}
    层高: ${data.ceilingHeight}
    水电性质: ${data.utilitiesType === 'commercial' ? '商水商电' : '民水民电'}
    物业费: ${data.propertyFee || '未知'}
    预计开业: ${data.openingDate}
    用户特别备注(已知竞品/背景): ${data.customInfo || '无'}
    `;

    const success = await initializeChat(context);
    
    if (success) {
      setStage(StepStage.MARKET_INTEL);
      // Explicitly pass location into Step 1 extra input to ensure map data is accurate
      const step1Context = `请基于项目位置【${data.location}】进行详细侦察。` + (data.customInfo ? ` 用户备注：${data.customInfo}` : "");
      await executeStep(StepStage.MARKET_INTEL, step1Context, data.marketDataFile || undefined);
    } else {
      alert("无法连接到 AI 服务。请检查您的网络或 API Key 配置。");
      setIsLoading(false);
    }
  };

  const processUploadedFiles = async (files: File[]): Promise<{ textAppended?: string; base64?: string; mimeType?: string }> => {
    const MAX_TEXT_LENGTH = 50000; 
    let combinedText = "";
    let finalBase64: string | undefined = undefined;
    let finalMimeType: string | undefined = undefined;

    for (const file of files) {
        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        const isCsv = fileName.endsWith('.csv');
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';

        if (isExcel) {
            if (!window.XLSX) continue;
            try {
                const arrayBuffer = await file.arrayBuffer();
                const data = new Uint8Array(arrayBuffer);
                const workbook = window.XLSX.read(data, { type: 'array' });
                let fileText = `\n\n=== 文件: ${file.name} ===`;
                workbook.SheetNames.forEach((sheetName: string) => {
                    const sheet = workbook.Sheets[sheetName];
                    const csvText = window.XLSX.utils.sheet_to_csv(sheet);
                    if (csvText && csvText.trim().length > 0) {
                        fileText += `\n--- Sheet: ${sheetName} ---\n${csvText}`;
                    }
                });
                combinedText += fileText;
            } catch (err) { console.error(err); }
        }
        else if (isCsv || file.type.startsWith('text/')) {
            try {
                const text = await file.text();
                combinedText += `\n\n=== 文件: ${file.name} ===\n${text}`;
            } catch (err) { console.error(err); }
        }
        else if (isImage || isPdf) {
            try {
                const reader = new FileReader();
                const result = await new Promise<string>((resolve, reject) => {
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                finalBase64 = result.split(',')[1];
                finalMimeType = file.type;
            } catch (err) { console.error(err); }
        }
    }

    if (combinedText.length > MAX_TEXT_LENGTH) {
        combinedText = combinedText.substring(0, MAX_TEXT_LENGTH) + "\n\n[系统提示: 多文件数据过长，已自动截断]";
    }

    return {
        textAppended: combinedText.length > 0 ? `\n\n[用户上传的参考资料数据汇总]:${combinedText}` : undefined,
        base64: finalBase64,
        mimeType: finalMimeType
    };
  };

  const executeStep = async (
      currentStage: StepStage, 
      extraInput?: string, 
      inputFiles?: File[] | File, 
      isRegeneration: boolean = false,
      financialParamsOverride?: FinancialParams
  ) => {
    setIsLoading(true);
    
    let files: File[] = [];
    if (inputFiles) {
        files = Array.isArray(inputFiles) ? inputFiles : [inputFiles];
    }

    let prompt = "";
    
    if (isRegeneration && extraInput) {
        prompt = `用户反馈 (CRITICAL FEEDBACK): "${extraInput}"。\n\n之前的分析存在偏差或错误，请完全基于用户的最新反馈，**重新执行** Step ${currentStage}。请务必修正地理位置或相关参数，不要重复之前的错误。`;
    } else {
        prompt = STEP_PROMPTS[currentStage as keyof typeof STEP_PROMPTS];
        
        // Add context for Step 1 if passed via extraInput
        if (currentStage === StepStage.MARKET_INTEL && extraInput) {
             prompt = `${extraInput}\n\n` + prompt;
        }
        
        if (currentStage === StepStage.DESIGN_VALIDATION) {
             if (extraInput) {
                 prompt = `用户已提供方案描述："${extraInput}"。\n\n` + prompt;
             }
        } else if (currentStage === StepStage.FINANCIAL_MODEL) {
            prompt = STEP_PROMPTS[7]; 
            const activeParams = financialParamsOverride || financialParams;

            let rampUpContext = "";
            if (projectData && projectData.openingDate) {
                const openDate = new Date(projectData.openingDate);
                const month = openDate.getMonth() + 1; 
                // Define Off-Peak: November, December, January, February
                const isOffPeak = (month >= 11 || month <= 2); 
                
                let unitCount = 0;
                if (projectData.scale.includes('间') || projectData.scale.includes('units')) {
                    unitCount = parseInt(projectData.scale.replace(/[^0-9]/g, '')) || 100;
                } else {
                    const area = parseInt(projectData.scale.replace(/[^0-9]/g, '')) || 0;
                    unitCount = Math.floor(area / 35); 
                }

                const isLargeScale = unitCount > 200;
                let rampUpRationale = "";
                let targetYear1Occ = "85%"; 
                let pricingStrategyContext = "";

                // --- 1. Occupancy Ramp-up Logic ---
                if (isOffPeak && isLargeScale) {
                    rampUpRationale = `开业时间为 ${month}月 (传统淡季)，且项目体量较大 (>200间)，双重压力叠加，去化极慢。`;
                    targetYear1Occ = "65%-70%";
                } else if (isOffPeak) {
                    rampUpRationale = `开业时间为 ${month}月 (传统淡季)，受春节返乡潮影响，前3个月几乎无自然来访。`;
                    targetYear1Occ = "70%-75%";
                } else if (isLargeScale) {
                    rampUpRationale = `项目体量较大 (>200间)，市场吸纳需要更长周期。`;
                    targetYear1Occ = "75%-80%";
                } else {
                    rampUpRationale = `开业时间为 ${month}月 (租赁旺季)，且体量适中，爬坡迅速。`;
                    targetYear1Occ = "80%-85%";
                }

                // --- 2. Rent Pricing Strategy Logic ---
                if (isOffPeak) {
                    pricingStrategyContext = `
                    **【淡季开业价格策略 (Winter Opening Strategy)】**:
                    由于项目在 ${month}月 (淡季) 入市，为保生存必须采取【以价换量】策略。
                    **指令**: 请强制将 Year 1 的【平均单房租金】在 Step 5.5 的定价基础上 **下调 3%-5%** 作为“开业特惠价” (Opening Promo)。Year 2 再恢复至标准市场价。
                    `;
                } else {
                    pricingStrategyContext = `
                    **【旺季开业价格策略 (Peak Season Strategy)】**:
                    项目在 ${month}月 (旺季) 入市，市场需求强劲。
                    **指令**: Year 1 租金维持 Step 5.5 的标准定价，无需打折，直接吃满溢价。
                    `;
                }

                // --- Combine Context ---
                rampUpContext = `
                【季节性与开业时间影响深度修正 (Seasonality Impact)】
                基于项目开业日期 (${projectData.openingDate}) 分析：
                1. 季节属性: ${isOffPeak ? "⚠️ 淡季 (Off-Peak / Winter)" : "✅ 旺季 (Peak Season)"}
                2. 预估体量: 约 ${unitCount} 间
                
                **修正指令 A (出租率)**: 
                - 理由: ${rampUpRationale}
                - **执行**: Year 1 (爬坡期) 年度平均出租率强制设定为 **${targetYear1Occ}**。

                **修正指令 B (租金策略)**:
                ${pricingStrategyContext}
                `;
            }

            if (activeParams) {
                let financialContext = '';
                
                if (activeParams.leaseModel === 'light_asset') {
                     financialContext = `
                     【用户强制指定的商务条件 - 轻资产委托模式】
                     - 模式: 纯受托管理 (Light Asset)
                     - 合作/委托年限: ${activeParams.leaseTerm} 年
                     - 改造投入: 0 (业主承担或现状交付)
                     - 管理费收取标准: 营收(GPR)的 ${activeParams.revenueShare}% + NOI的 ${activeParams.noiShare}%
                     - **营收(GPR)增长假设**: 必须严格执行【${activeParams.revenueGrowthRate}】，严禁臆测其他数值。
                     - **重要**: 此模式下，表格中的“底租支出”项应替换为“管理费支出 (Manager Fee)”。Net Cash Flow 代表“业主净收益 (Owner Net Income)”。
                     - **表格要求**: 必须在表格中增加两行：【管理方收入】和【业主方净收入】。
                     `;
                } else {
                    const isFixed = activeParams.leaseModel === 'fixed';
                    const leaseDesc = isFixed 
                        ? `固定单价: ${activeParams.leaseUnitValue} 元/㎡/月` 
                        : `营收分成: ${activeParams.leaseUnitValue}%`;

                    financialContext = `
                    【用户强制指定的商务条件 - 包租模式】
                    - 改造单方成本: ${activeParams.capexPerSqm} 元/㎡
                    - 底租模式及标准: ${leaseDesc}
                    - 免租期: ${activeParams.rentFreePeriod} 个月
                    - 租期: ${activeParams.leaseTerm} 年
                    - 底租递增条款: ${activeParams.rentEscalation}
                    - **营收(GPR)增长假设**: 必须严格执行【${activeParams.revenueGrowthRate}】，严禁臆测其他数值。
                    `;
                }

                if (activeParams.customFinancials) {
                    financialContext += `\n\n【用户补充的财务/税务/融资信息】\n${activeParams.customFinancials}`;
                }
                
                financialContext += rampUpContext;
                prompt = prompt.replace('[USER_FINANCIAL_PARAMS_PLACEHOLDER]', financialContext);
            }
        } else if (currentStage === StepStage.FINAL_REPORT) {
            prompt = STEP_PROMPTS[8]; 
        }
    }

    let imageBase64: string | undefined = undefined;
    let mimeType: string | undefined = undefined;

    if (files.length > 0) {
         try {
            const result = await processUploadedFiles(files);
            if (result.textAppended) {
                prompt += result.textAppended;
            }
            if (result.base64) {
                imageBase64 = result.base64;
                mimeType = result.mimeType;
            }
        } catch (e) {
            console.error("File processing failed", e);
            alert("文件处理失败。请确保上传有效的 Excel, CSV, PDF 或图片文件。");
            setIsLoading(false);
            return;
        }
    }

    const response = await sendMessageToGemini(prompt, imageBase64, mimeType);

    const newResult: StepResult = {
      stepId: currentStage,
      title: getStepTitle(currentStage),
      content: response,
      timestamp: Date.now()
    };

    setResults(prev => {
        if (isRegeneration) {
            const existingIndex = prev.findIndex(r => r.stepId === currentStage);
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = newResult;
                return updated;
            }
        }
        const existingIndex = prev.findIndex(r => r.stepId === currentStage);
        if (existingIndex >= 0) {
             const updated = [...prev];
             updated[existingIndex] = newResult;
             return updated;
        }
        return [...prev, newResult];
    });
    
    setIsLoading(false);
  };

  const handleAIGeneratePlan = async (baseFile: File, styleFile?: File, customPrompt?: string): Promise<string | null> => {
      if (!baseFile) return null;
      try {
          const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve((reader.result as string).split(',')[1]);
              reader.onerror = error => reject(error);
          });

          const base64Plan = await fileToBase64(baseFile);
          let base64Style = undefined;
          if (styleFile) {
              base64Style = await fileToBase64(styleFile);
          }
          const step5Result = results.find(r => r.stepId === StepStage.DESIGN_BRIEF);
          const briefText = step5Result ? step5Result.content : "No Design Brief found.";
          return await generateAIFloorPlan(base64Plan, base64Style, briefText, customPrompt);
      } catch (e) {
          console.error("Plan Generation Failed", e);
          return null;
      }
  };

  const handleNext = () => {
    let nextStage = stage + 1;
    const hasNextResult = results.some(r => r.stepId === nextStage);

    if (stage === StepStage.DESIGN_BRIEF) {
       setStage(StepStage.DESIGN_VALIDATION);
       return; 
    }
    setStage(nextStage);
    if (!hasNextResult) {
        executeStep(nextStage);
    }
  };

  const handlePrevious = () => {
      if (stage === StepStage.MARKET_INTEL) {
          setStage(StepStage.INIT);
          setResults([]); 
          setProjectData(null);
      } else if (stage > StepStage.INIT) {
          setStage(stage - 1);
          if (isCollectingFinancials) {
              setIsCollectingFinancials(false);
              setStage(StepStage.DESIGN_VALIDATION);
          }
      }
  };

  const handleReset = () => {
      // Force stop loading
      setIsLoading(false);
      
      // Attempt to clear state gracefully
      setStage(StepStage.INIT);
      setProjectData(null);
      setFinancialParams(null);
      setIsCollectingFinancials(false);
      setResults([]);
      setUploadedFiles([]);

      // Scroll to top
      window.scrollTo(0, 0);

      // Fallback: If for some reason the UI is stuck (rare in React but possible with complex state), user can manually refresh.
      // But this function logic is now sound.
  };

  const handleFeedback = (feedback: string, file?: File) => {
      executeStep(stage, feedback, file ? [file] : undefined, true);
  };

  const handleUploadAndProceed = async (files?: File[] | File, text?: string) => {
      const filesArray = files ? (Array.isArray(files) ? files : [files]) : [];
      if (filesArray.length > 0) {
          setUploadedFiles(prev => [...prev, ...filesArray]);
          for(const f of filesArray) {
             if (f.type.startsWith('image/')) {
                 try {
                     const reader = new FileReader();
                     reader.onload = (e) => {
                         const result = e.target?.result as string;
                         const base64 = result.split(',')[1];
                         setProjectData(prev => prev ? ({ ...prev, designImageBase64: base64 }) : null);
                     };
                     reader.readAsDataURL(f);
                     break; 
                 } catch(e) { console.error(e); }
             }
          }
      }
      setStage(StepStage.DESIGN_VALIDATION);
      executeStep(StepStage.DESIGN_VALIDATION, text, filesArray);
  };
  
  const handleProceedToFinancial = () => {
      setIsCollectingFinancials(true);
  };

  const handleFinancialParamsSubmit = (params: FinancialParams) => {
      setFinancialParams(params);
      setIsCollectingFinancials(false);
      setStage(StepStage.FINANCIAL_MODEL);
      executeStep(StepStage.FINANCIAL_MODEL, undefined, undefined, false, params);
  };

  const handleProceedToReport = () => {
      const nextStage = StepStage.FINAL_REPORT;
      setStage(nextStage);
      const hasResult = results.some(r => r.stepId === nextStage);
      if (!hasResult) executeStep(nextStage);
  };

  const downloadReport = () => {
      if (!projectData) return;
      const html = generateHtmlReport(projectData, results, financialParams);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Asset_Plan_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
  };

  const handleCopyReport = async () => {
      if (!projectData) return;
      const html = generateHtmlReport(projectData, results, financialParams);
      try {
          const blobHtml = new Blob([html], { type: 'text/html' });
          const blobText = new Blob([html], { type: 'text/plain' });
          const data = [new ClipboardItem({
              ["text/html"]: blobHtml,
              ["text/plain"]: blobText
          })];
          await navigator.clipboard.write(data);
      } catch (err) {
          console.error('Clipboard write failed', err);
          try {
             await navigator.clipboard.writeText(html);
          } catch(e) {
             alert("复制失败，请尝试下载HTML文件");
          }
      }
  };

  const getStepTitle = (s: StepStage) => {
    switch (s) {
        case StepStage.MARKET_INTEL: return "Step 1: 市场及竞品侦察";
        case StepStage.PERSONA_GRAVITY: return "Step 2: 客群画像与引力法则";
        case StepStage.PRICING_GAME: return "Step 3: 双轨博弈定价";
        case StepStage.PRODUCT_STRATEGY: return "Step 4: 产品策略与坪效";
        case StepStage.DESIGN_BRIEF: return "Step 5: 强排设计任务书";
        case StepStage.DESIGN_VALIDATION: return "Step 5.5: 方案博弈与重定价";
        case StepStage.FINANCIAL_MODEL: return "Step 6: 经营测算及现金流";
        case StepStage.FINAL_REPORT: return "Step 7: 总结备忘";
        default: return "";
    }
  };

  if (stage === StepStage.INIT) {
    return (
        <div className="min-h-screen bg-slate-50">
            <InitForm onSubmit={handleInit} isLoading={isLoading} />
        </div>
    );
  }

  const visibleResults = results.filter(r => r.stepId <= stage).sort((a, b) => a.stepId - b.stepId);

  return (
    <div className="min-h-screen bg-slate-50 pb-40 print:pb-0 print:bg-white">
      <div className="sticky top-0 z-40 bg-white shadow-sm print:hidden">
         <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-slate-800">
                <Lightbulb className="w-6 h-6 text-blue-600" />
                <span className="font-bold text-lg hidden md:inline">长租公寓资产策划顾问</span>
            </div>
            <div className="text-sm text-gray-500 font-mono flex items-center space-x-2">
                <span className="bg-slate-100 px-2 py-1 rounded text-slate-600 font-bold">{projectData?.projectName}</span>
                <span className="hidden sm:inline">| {projectData?.location.substring(0, 15)}...</span>
            </div>
         </div>
         <StepIndicator currentStage={stage} />
      </div>

      <main className="max-w-5xl mx-auto p-4 md:p-8 space-y-8 print:p-0 print:space-y-4 print:w-full print:max-w-none">
        
        <div className="hidden print:block mb-8 border-b-2 border-slate-900 pb-4">
             <h1 className="text-3xl font-bold text-slate-900">{projectData?.projectName}</h1>
             <p className="text-sm text-slate-500 mt-2">资产管理策划报告 | 生成日期: {new Date().toLocaleDateString()}</p>
        </div>

        {visibleResults.map((res) => (
            <div key={res.stepId} id={`step-container-${res.stepId}`} className="animate-fade-in-up print:animate-none print:break-inside-avoid">
                <div className="flex items-center space-x-2 mb-3 print:mb-2">
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded print:border print:border-blue-900 print:bg-white print:text-blue-900">
                        {getStepTitle(res.stepId).split(':')[0]}
                    </span>
                    <h2 className="text-lg font-bold text-gray-800 print:text-black">{getStepTitle(res.stepId).split(':')[1]}</h2>
                </div>
                <MarkdownRenderer content={res.content} />
            </div>
        ))}
        
        {isCollectingFinancials && (
            <div className="print:hidden">
                <FinancialParamsForm 
                    onSubmit={handleFinancialParamsSubmit} 
                    defaultArea={projectData?.scale ? projectData.scale.replace(/[^0-9]/g, '') : ''}
                />
            </div>
        )}

        {isLoading && (
            <div className="animate-pulse space-y-4 p-6 bg-white rounded-xl border border-gray-100 print:hidden">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
        )}
      </main>

      {!isCollectingFinancials && (
          <StepActions 
            stage={stage}
            isThinking={isLoading}
            onNext={() => {
                if (stage === StepStage.DESIGN_VALIDATION) {
                    handleProceedToFinancial();
                } else if (stage === StepStage.FINANCIAL_MODEL) {
                    handleProceedToReport();
                } else {
                    handleNext();
                }
            }}
            onPrevious={handlePrevious}
            onFeedback={handleFeedback}
            onUpload={handleUploadAndProceed}
            onGeneratePlan={handleAIGeneratePlan}
            onDownloadReport={downloadReport}
            onDownloadPdf={handleCopyReport} 
            onReset={handleReset}
          />
      )}
    </div>
  );
};

export default App;
